import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { ProvidersService } from '../../services/ProvidersService';
import CropPhotoModal from '../../components/CropPhotoModal';
import './uploadPhotosTab.css';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import DragItem from '../../components/DragItem';
import DropZone from '../../components/DropZone';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Constants } from '../../Constants';
import UseWindowDimensions from "../../components/GetWindowDimensions";
import { getConfig } from '../../configService';

export function UploadPhotosTab({ providerId }) {
  const { keycloak } = useKeycloak();
  const [imageList, setImageList] = useState([]);
  const [thumbnail, setThumbnail] = useState('');
  const [showCropModal, setShowCropModal] = useState(false);
  const [newImageFile, setNewImageFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [dragItems, setDragItems] = useState([]);
  const [dragItemsUpdated, setDragItemsUpdated] = useState(1);
  const { width } = UseWindowDimensions(); // Use a custom hook to get window dimensions
  const isMobile = width < Constants.MOBILE_WIDTH;
  // Use the provider's actual ID (passed from profile.jsx which loads via /provider/me),
  // so claimed providers whose id != keycloak sub work correctly.
  const subscriberId = providerId || keycloak.tokenParsed.sub;
  const config = getConfig();

  const refreshData = () => {
    ProvidersService.getSingleProvider(subscriberId).then((response) => {
      const array = response.imageList.map((imageId) => ({
        imageId,
        isSelected: false,
        showOptions: false,
      }));
      setImageList(array);
      setThumbnail(response.thumbnail || (array.length === 1 ? array[0].imageId : ''));
    });
  };

  useEffect(() => {
    refreshData();
  }, [subscriberId]);

  useEffect(() => {
    buildDragItemList();
  }, [imageList]);

  const handleDragEnd = (draggedImageId) => {
    console.log('Drag ended:', draggedImageId);
  };

  const moveImageToNewIndex = async (imageId, newIndex) => {
    await ProvidersService.MoveImage(subscriberId, imageId, newIndex);
    refreshData();
  };

  const uploadImage = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setImageSrc(reader.result);
      };
      setNewImageFile(file);
      setShowCropModal(true);
    }
  };

  const handleCropComplete = async (croppedImage) => {
    try {
      const result = await ProvidersService.generateUploadURL(subscriberId);
      await ProvidersService.uploadPhotoToStorage(result.data.uploadUrl, croppedImage);
      await ProvidersService.completeUploadJob(subscriberId, result.data.imageId);
      refreshData();
    } catch (error) {
      console.error(error);
    } finally {
      setShowCropModal(false);
    }
  };

  const deleteImage = async (imageId) => {
    try {
      await ProvidersService.deleteImage(subscriberId, imageId);
      const newImageList = imageList.filter((image) => image.imageId !== imageId);
      setImageList(newImageList);
      if (thumbnail === imageId) {
        const newThumbnail = newImageList.length > 0 ? newImageList[0].imageId : '';
        setThumbnail(newThumbnail);
        await ProvidersService.updateProviderThumbnail(subscriberId, newThumbnail);
      }
      refreshData();
    } catch (error) {
      console.error(error);
    }
  };

  const handleOptionsClick = (e, item) => {
    e.stopPropagation();
    setImageList((prevImageList) =>
      prevImageList.map((image) =>
        image.imageId === item.imageId
          ? { ...image, showOptions: !image.showOptions }
          : { ...image, showOptions: false }
      )
    );
  };

  const saveAsCoverPhoto = async (item) => {
    try {
      await ProvidersService.updateProviderThumbnail(subscriberId, item.imageId);
      setImageList((prevImageList) => {
        const newList = prevImageList.filter((image) => image.imageId !== item.imageId);
        return [{ ...item, showOptions: false }, ...newList];
      });
      await moveImageToNewIndex(item.imageId, 0);
      handleOptionsClick(null, item);
    } catch (error) {
      console.error('Error saving as cover photo:', error);
    }
  };

  const deletePhoto = async (item) => {
    try {
      await deleteImage(item.imageId);
      handleOptionsClick(null, item); // Close the dropdown after action
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  const buildDragItemList = () => {
    const items = imageList.map((item, index) => (
      <DragItem key={item.imageId} image={item.imageId} index={index} handleDragEnd={handleDragEnd}>
        <DropZone key={item.imageId} currentImageIndex={index} imageList={imageList} moveImageToNewIndex={moveImageToNewIndex}>
          <div
            className="photo-container"
            style={{
              border: item.isSelected ? '5px solid #A6CEEF' : '',
            }}
          >
            <img
              src={`${config.imageBaseUrl}${subscriberId}/${item.imageId}-large.jpg`}
              alt="provider thumbnail"
              className="photo-album-image"
            />
            {thumbnail === item.imageId && <div className="cover-photo-watermark">Cover Photo</div>}
            <div className="photo-options" onClick={(e) => handleOptionsClick(e, item)}>
              ⋮
            </div>
            {item.showOptions && (
              <div className="options-dropdown">
                <button onClick={() => saveAsCoverPhoto(item)}>Make Cover Photo</button>
                <button onClick={() => deletePhoto(item)}>Delete Photo</button>
              </div>
            )}
          </div>
        </DropZone>
      </DragItem>
    ));
    setDragItems(items);
    setDragItemsUpdated(dragItemsUpdated + 1);
  };

  const moveImageUp = async (index) => {
    if (index > 0) {
      const newIndex = index - 1;
      const imageId = imageList[index].imageId;
      await moveImageToNewIndex(imageId, newIndex);
    }
  };

  const moveImageDown = async (index) => {
    if (index < imageList.length - 1) {
      const newIndex = index + 1;
      const imageId = imageList[index].imageId;
      await moveImageToNewIndex(imageId, newIndex);
    }
  };

  const renderMobileView = () => (
    <div className="mobile-photos-container">
      {imageList.map((item, index) => (
        <div key={item.imageId} className="photo-container mobile">
          <img
            src={`${config.imageBaseUrl}${subscriberId}/${item.imageId}-large.jpg`}
            alt="provider thumbnail"
            className="photo-album-image"
          />
          {thumbnail === item.imageId && <div className="cover-photo-watermark">Cover Photo</div>}
          <div className="move-buttons">
            <IconButton
              onClick={() => moveImageUp(index)}
              disabled={index === 0}
              className="move-button"
            >
              <KeyboardArrowUpIcon style={{ color: 'blue' }} />
            </IconButton>
            <IconButton
              onClick={() => moveImageDown(index)}
              disabled={index === imageList.length - 1}
              className="move-button"
            >
              <KeyboardArrowDownIcon style={{ color: 'blue' }} />
            </IconButton>
          </div>
          <div className="photo-options" onClick={(e) => handleOptionsClick(e, item)}>
            ⋮
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Box sx={{ p: '5px', pb: '25px' }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Photos
      </Typography>
      <div className="container">
        <Box sx={{ marginTop: 3, marginLeft: 'auto', marginRight: 'auto', marginBottom: 4, display: 'table', textAlign: 'center' }}>
          <label htmlFor="file-upload">
            <Button
              component="span"
              variant="outlined"
              startIcon={<FileUploadIcon />}
            >
              Add New Image
            </Button>
          </label>
          <input
            type="file"
            id="file-upload"
            onChange={uploadImage}
            name="thumbnail"
            accept=".jpg"
            multiple={false}
            style={{ display: 'none' }}
          />
        </Box>

        <DndProvider backend={HTML5Backend}>
          {isMobile ? renderMobileView() : (
            <div key={dragItemsUpdated} className="image-grid">
              {dragItems}
            </div>
          )}
        </DndProvider>

        {showCropModal && (
          <CropPhotoModal
            imageSrc={imageSrc}
            setImageSrc={setImageSrc}
            setShowModal={setShowCropModal}
            onCropComplete={handleCropComplete}
            newImageFile={newImageFile}
          />
        )}

      </div>
    </Box>
  );
}

export default UploadPhotosTab;


